// What a person watches while a pricing sheet is being read.
//
// The design problem is that the honest steps are uneven. An accurate-mode
// conversion of a scanned 24-page pricelist is minutes with nothing to report
// inside it; reading 6 tables is seconds. A single smooth bar over that either
// crawls and looks stuck or races and looks fake.
//
// So the STEP LIST is the primary signal and the bar is secondary. The named
// steps are the pipeline's real phases, each one either done, current, or not
// started — a person can see which of the four they are waiting on and roughly
// how long the rest should take. The percentage only ever moves on completed
// work, so it may legitimately sit still.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code as ConnectCode, ConnectError } from '@connectrpc/connect';
import { IntelligenceTypes } from '@mono/proto-clients';
import { SpecraBadge, SpecraButton, SpecraInlineBanner, SpecraProgress, SpecraSpinner, SpecraSurface } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { isRunSettled, runIsStale, useCancelIntelligenceRun, useIntelligenceRunProgress } from '../_shared/hooks/intelligenceHooks';
const State = IntelligenceTypes.IntelligenceRunState;
// The steps in the order they happen, named as a reviewer would describe them
// rather than as the code is structured. CONVERTING is listed even though a
// spreadsheet skips it — a step that is skipped renders as skipped, which is more
// informative than a list that changes shape depending on the file type.
const STEPS = [
    {
        state: State.CONVERTING,
        label: 'Convert the document'
    },
    {
        state: State.RECONCILING,
        label: 'Match labor categories'
    },
    {
        state: State.EXTRACTING,
        label: 'Read the rate tables'
    },
    {
        state: State.READY_FOR_REVIEW,
        label: 'Stage the review'
    }
];
// Rank by pipeline order so a step earlier than the current one reads as done.
const ORDER = new Map([
    [
        State.QUEUED,
        0
    ],
    [
        State.UPLOADING,
        1
    ],
    [
        State.CONVERTING,
        2
    ],
    [
        State.OCR,
        2
    ],
    [
        State.RECONCILING,
        3
    ],
    [
        State.CLASSIFYING,
        4
    ],
    [
        State.EXTRACTING,
        4
    ],
    [
        State.READY_FOR_REVIEW,
        5
    ],
    [
        State.PARTIALLY_READY,
        5
    ]
]);
function statusOf(step, current) {
    if (current === undefined) {
        return 'waiting';
    }
    const here = ORDER.get(step) ?? 0;
    const now = ORDER.get(current) ?? 0;
    if (now > here) {
        return 'done';
    }
    return now === here ? 'current' : 'waiting';
}
export function PricingSheetIngestProgress({ filename, onSettled, runId }) {
    const { data: run, error, isError } = useIntelligenceRunProgress(runId);
    const cancel = useCancelIntelligenceRun();
    const navigate = useNavigate();
    const state = run?.state;
    const settled = isRunSettled(state);
    const failed = state === State.FAILED;
    // Stopped telling us anything, which is NOT the same as failed — and must not
    // be shown as progress. A run stranded mid-flight polled forever and read as
    // "Queued…", so the one thing this must not do is stay quiet about it.
    const stalled = runIsStale(run);
    // Where the backend's live sentence goes. It belongs NEXT TO the step it
    // describes — that is the whole point of naming the steps — but "Queued…" and
    // the final "Ready for review — 420 rates" describe no step in the list, so
    // those stay in the header rather than vanishing.
    const currentStepInList = !settled && STEPS.some(({ state: st })=>statusOf(st, state) === 'current');
    // In an effect, and ONCE. Calling this during render fires on every render —
    // and since onSettled is typically a query invalidation, that is a refetch
    // loop that looks like the backend hammering itself.
    const announced = useRef(false);
    useEffect(()=>{
        if (!settled || announced.current) {
            return;
        }
        announced.current = true;
        // Said out loud, because an ingest runs for minutes and a person will be
        // looking at something else when it lands. Without this, finishing is
        // indistinguishable from still going unless you happen to be watching.
        if (state === State.FAILED) {
            toast.error(`${filename} could not be read`, {
                description: run?.currentStep
            });
        } else {
            toast.success(`${filename} is ready to review`, {
                description: run?.currentStep,
                // The card disappears once it settles, so the toast is the only route to
                // the thing that was just produced.
                action: {
                    label: 'Review',
                    onClick: ()=>navigate('/registries?tab=pricing-sheets')
                }
            });
        }
        onSettled?.();
    }, [
        filename,
        navigate,
        onSettled,
        run?.currentStep,
        settled,
        state
    ]);
    // The error CODE decides the sentence, because the three errors mean three
    // different things and only one of them is the run's fault. This used to say
    // "its run record is gone" for every error — and the first live upload proved
    // how expensive that shorthand is: the server was returning PERMISSION_DENIED
    // (a missing workflow-kind area mapping), the record existed the whole time,
    // and the wrong message sent the investigation toward a create/poll race that
    // did not exist. An error collapsed into one message is an absence reported
    // as an answer.
    if (isError) {
        const code = ConnectError.from(error).code;
        const gone = code === ConnectCode.NotFound;
        const forbidden = code === ConnectCode.PermissionDenied;
        return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
            action: /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>onSettled?.(),
                variant: "outline",
                children: "Dismiss"
            }),
            "data-testid": Testids.pricingSheetReview.ingestProgress,
            tone: gone ? 'neutral' : 'warning',
            children: gone ? `${filename} is no longer being tracked — its run record is gone.` : forbidden ? `${filename} is running, but you don't have access to watch it — ` + 'watching a run takes the same registries authority as reviewing its results.' : `${filename}: progress can't be fetched right now — still retrying.`
        });
    }
    if (failed) {
        return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
            action: // A failure a person cannot clear is a permanent accusation. The run
            // record stays; only the watch list entry goes.
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>onSettled?.(),
                variant: "outline",
                children: "Dismiss"
            }),
            "data-testid": Testids.pricingSheetReview.ingestProgress,
            tone: "danger",
            children: [
                filename,
                ": ",
                run?.currentStep || 'The ingest failed.'
            ]
        });
    }
    if (stalled) {
        return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
            action: /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>onSettled?.(),
                variant: "outline",
                children: "Dismiss"
            }),
            "data-testid": Testids.pricingSheetReview.ingestProgress,
            tone: "warning",
            children: [
                filename,
                " stopped responding after “",
                run?.currentStep || 'Queued',
                "”. It has not reported for over five minutes, so it is no longer being watched — re-read the sheet to try again."
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(SpecraSurface, {
        className: "p-4",
        "data-testid": Testids.pricingSheetReview.ingestProgress,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex min-w-0 items-center gap-2",
                        children: [
                            settled ? null : /*#__PURE__*/ _jsx(SpecraSpinner, {}),
                            /*#__PURE__*/ _jsx("span", {
                                className: "shrink-0 font-medium",
                                children: filename
                            }),
                            currentStepInList ? null : /*#__PURE__*/ _jsx("span", {
                                className: "truncate text-[length:var(--fs-ui-body)]",
                                children: run?.currentStep || 'Queued…'
                            })
                        ]
                    }),
                    settled ? /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: state === State.PARTIALLY_READY ? 'warning' : 'success',
                        children: state === State.PARTIALLY_READY ? 'Ready, with gaps' : 'Ready for review'
                    }) : // Cooperative: this writes CANCELLED and the worker stops at its next
                    // phase boundary, so the button reports "Stopping…" rather than
                    // claiming the work has already halted.
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.pricingSheetReview.cancelIngest,
                        disabled: cancel.isPending || state === State.CANCELLED,
                        onClick: ()=>cancel.mutate(runId),
                        variant: "outline",
                        children: cancel.isPending || state === State.CANCELLED ? 'Stopping…' : 'Cancel'
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mt-3",
                children: /*#__PURE__*/ _jsx(SpecraProgress, {
                    "aria-label": "Pricing sheet ingest progress",
                    value: settled ? 100 : run?.progressPercent ?? 0
                })
            }),
            /*#__PURE__*/ _jsx("ol", {
                className: "mt-3 space-y-1",
                children: STEPS.map(({ label, state: stepState })=>{
                    const status = settled ? 'done' : statusOf(stepState, state);
                    return /*#__PURE__*/ _jsxs("li", {
                        className: status === 'current' ? 'flex items-center gap-2 text-[length:var(--fs-ui-caption)] font-medium' : 'flex items-center gap-2 text-[length:var(--fs-ui-caption)]',
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                "aria-hidden": true,
                                className: status === 'done' ? 'text-success' : status === 'current' ? 'text-foreground' : 'text-muted-foreground',
                                children: status === 'done' ? '✓' : status === 'current' ? '•' : '·'
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: status === 'waiting' ? 'text-muted-foreground' : undefined,
                                children: label
                            }),
                            status === 'current' && run?.currentStep ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        "aria-hidden": true,
                                        className: "text-muted-foreground",
                                        children: "—"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "truncate text-muted-foreground",
                                        children: run.currentStep
                                    })
                                ]
                            }) : null
                        ]
                    }, label);
                })
            })
        ]
    });
}
